import React, { useCallback, useRef } from 'react';

import {
  LTBox,
  LTIconButton,
  LTLoader,
  LTTestStatusLabel,
  LTText,
  LTTooltip,
} from '@lambdatestincprivate/lt-components';
import { themeGet } from '@primer/react';
import capitalize from 'lodash/capitalize';
import styled from 'styled-components';

import { TestStatusNameMap } from '../../constants/test';
import { FlakyTestIcon } from '../../assets/flaky-test';
import phone from '../../assets/phone.svg';
import resolutionIcon from '../../assets/resolution.svg';
import NewTab from '../../assets/newTab';
import { getStatusIconAndClassPrefix } from '../../utils/statusOptions';
import { isMobileDevice } from '../../utils/testHelpers';
import { getBrowserIcon, getOsIcon } from '../../utils/utilityFunctions';
import {
  convertTimeTaken,
  formatTimestampToDateAndTime,
} from '../../utils/dateTimeUtils';

const ColumnRowWrapper = styled.div`
  border: 1px solid ${themeGet('colors.border.default')};
  background-color: ${themeGet('colors.bg.muted')};
  border-bottom: none;
  display: flex;
  font-size: 12px;
  &:last-child {
    border-bottom: 1px solid ${themeGet('colors.border.default')};
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
  }
  &:first-child {
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
  }
`;

const DataRowWrapper = styled(ColumnRowWrapper)`
  background-color: ${({ $isSelectedTest }) =>
    $isSelectedTest
      ? themeGet('colors.bg.muted')
      : themeGet('colors.bg.default')};
  &:first-child {
    border-top: 0px;
    border-top-left-radius: 0px;
    border-top-right-radius: 0px;
  }
`;

const Cell = styled.div`
  padding: 6px 12px;
  display: flex;
  align-items: center;
  word-break: break-word;
  border-right: 1px solid ${themeGet('colors.border.default')};
  flex-wrap: wrap;
  white-space: normal;
  line-height: 1.4;
  &:last-child {
    border-right: none;
  }
`;

const ColumnCell = styled(Cell)`
  font-weight: 600;
  font-size: 12px;
  color: ${themeGet('colors.fg.muted')};
`;

const Dot = styled.span`
  display: inline-block;
  width: 2px;
  height: 2px;
  border-radius: 50%;
  background-color: ${themeGet('colors.fg.disabled')};
`;

const ConfigurationListItem = styled.li`
  align-items: center;
  display: flex;
  gap: 8px;
`;

const SectionHorizontal = styled(LTBox)`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const ItemContent = styled(LTBox)`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
`;

const Img = styled.img`
  height: 16px;
  width: 16px;
`;

const RowGroup = styled.div`
  overflow-y: auto;
  height: calc(100vh - 260px);
`;

const NoDataWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 16px;
`;

const ConfigList = styled.ul`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ScrollLoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 12px;
`;

const columns = [
  { name: 'Timestamp', value: 'timestamp', width: 18 },
  { name: 'Status', value: 'status', width: 16 },
  { name: 'Configuration', value: 'configuration', width: 30 },
  { name: 'Duration', value: 'duration', width: 10 },
  { name: 'Flaky %', value: 'flaky', width: 9 },
  { name: 'User', value: 'user', width: 12 },
  { name: '', value: 'redirection', width: 5 },
];

const getCellValue = (testData, cell, parentTestId) => {
  let {
    browser = '',
    browser_version = '',
    create_timestamp = '',
    device = '',
    duration = '',
    flake_rate = 0,
    os = '',
    os_version = '',
    resolution = '',
    status = '',
    test_id = '',
    username = '',
  } = testData || {};

  const osVersion = os_version?.toLowerCase?.().startsWith(os?.toLowerCase?.())
    ? os_version.slice(os.length).trim()
    : '';

  const browserVersion = browser_version
    ?.toLowerCase?.()
    .startsWith(browser?.toLowerCase?.())
    ? browser_version.slice(browser.length).trim()
    : '';

  const isFlakyTest = flake_rate && Number(flake_rate) > 0;

  let browserIcon = getBrowserIcon(browser);
  let osIcon = getOsIcon(os);
  const mobile = isMobileDevice(testData);

  if (mobile) {
    browserIcon = phone;
    os_version = device;
  }

  const { statusIconName } = getStatusIconAndClassPrefix(status);

  switch (cell) {
    case 'timestamp':
      return (
        <LTText variant={LTText.TextVariant.SUBHEADER_REGULAR}>
          {formatTimestampToDateAndTime(create_timestamp) || '-'}
        </LTText>
      );

    case 'status':
      return (
        <SectionHorizontal>
          <span aria-hidden="true">
            <LTTestStatusLabel
              iconSrc={statusIconName || ''}
              status={status}
              showStatusText={false}
              width={12}
              height={12}
            />
          </span>
          <LTText variant={LTText.TextVariant.SUBHEADER_REGULAR}>
            {TestStatusNameMap[status] || capitalize(status)}
          </LTText>
        </SectionHorizontal>
      );

    case 'flaky':
      return (
        <SectionHorizontal>
          <FlakyTestIcon color={isFlakyTest ? '#ED5F00' : '#8C959F'} />
          <LTText variant={LTText.TextVariant.SUBHEADER_REGULAR}>
            {isFlakyTest ? `${flake_rate}%` : '0'}
          </LTText>
        </SectionHorizontal>
      );

    case 'duration':
      return (
        <LTText variant={LTText.TextVariant.SUBHEADER_REGULAR}>
          {convertTimeTaken(duration) || '-'}
        </LTText>
      );

    case 'configuration':
      return (
        <SectionHorizontal as="span">
          <ConfigList>
            <ConfigurationListItem>
              <LTTooltip
                direction={LTTooltip.Direction.BOTTOM}
                text={capitalize(browser)}
              >
                <ItemContent>
                  {browserIcon && (
                    <Img src={browserIcon} alt="browser version" />
                  )}
                  <LTText variant={LTText.TextVariant.SUBHEADER_REGULAR}>
                    {browserVersion}
                  </LTText>
                </ItemContent>
              </LTTooltip>
              <Dot />
            </ConfigurationListItem>
            <ConfigurationListItem>
              <LTTooltip
                direction={LTTooltip.Direction.BOTTOM}
                text={capitalize(os)}
              >
                <ItemContent>
                  {osIcon && <Img src={osIcon} alt="os version" />}
                  <LTText variant={LTText.TextVariant.SUBHEADER_REGULAR}>
                    {osVersion}
                  </LTText>
                </ItemContent>
              </LTTooltip>
              <Dot />
            </ConfigurationListItem>
            {resolution && (
              <ConfigurationListItem>
                <LTTooltip
                  direction={LTTooltip.Direction.BOTTOM}
                  text="Resolution"
                >
                  <ItemContent>
                    <Img src={resolutionIcon} alt="" aria-hidden="true" />
                    <LTText variant={LTText.TextVariant.SUBHEADER_REGULAR}>
                      {resolution}
                    </LTText>
                  </ItemContent>
                </LTTooltip>
              </ConfigurationListItem>
            )}
          </ConfigList>
        </SectionHorizontal>
      );

    case 'user':
      return (
        <LTText variant={LTText.TextVariant.SUBHEADER_REGULAR}>
          {username || '-'}
        </LTText>
      );

    case 'redirection':
      return (
        <LTTooltip
          direction={LTTooltip.Direction.BOTTOM}
          text="Redirect to Test"
        >
          <LTIconButton
            aria-label="Open in a new tab"
            icon={() => <NewTab color="#333" />}
            onClick={() => {
              const params = new URLSearchParams(window.location.search);
              const details = params.get('details');
              const url = details
                ? `/test?testID=${test_id}&details=${details}`
                : `/test?testID=${test_id}`;
              window.open(url, '_blank');
            }}
            variant={LTIconButton.IconButtonVariants.INVISIBLE}
            size={LTIconButton.Size.SMALL}
          />
        </LTTooltip>
      );

    default:
      return null;
  }
};

const SCROLL_THRESHOLD = 80;

const HistoryList = ({
  data = [],
  isLoading = false,
  isSilentLoading = false,
  hasMore = false,
  parentTestId = '',
  onLoadMore,
}) => {
  const loadingRef = useRef(false);

  const handleScroll = useCallback(
    (e) => {
      if (!hasMore || loadingRef.current || isSilentLoading) return;
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      if (scrollHeight - scrollTop - clientHeight <= SCROLL_THRESHOLD) {
        loadingRef.current = true;
        onLoadMore?.();
        // Reset after a tick so subsequent scrolls can trigger again
        setTimeout(() => {
          loadingRef.current = false;
        }, 300);
      }
    },
    [hasMore, isSilentLoading, onLoadMore],
  );

  if (data.length === 0 && !isLoading) {
    return (
      <NoDataWrapper>
        <LTText variant={LTText.TextVariant.SUBHEADER_REGULAR} color="fg.muted">
          No tests found
        </LTText>
      </NoDataWrapper>
    );
  }

  return isLoading ? (
    <LTLoader heading="Loading Test History" />
  ) : (
    <div role="table" tabIndex={-1}>
      <div role="rowgroup">
        <ColumnRowWrapper
          role="row"
          style={{
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            fontWeight: 600,
            fontSize: '14px',
          }}
        >
          {columns.map((item, index) => (
            <ColumnCell
              key={index}
              role="columnheader"
              style={{ width: `${item.width}%` }}
            >
              {item?.name}
            </ColumnCell>
          ))}
        </ColumnRowWrapper>
      </div>
      <RowGroup role="rowgroup" onScroll={handleScroll}>
        {data.map((row, rowIndex) => (
          <DataRowWrapper
            role="row"
            key={row?.test_id ? `${row.test_id}-${rowIndex}` : rowIndex}
            $isSelectedTest={row?.test_id === parentTestId}
          >
            {columns.map((col, colIndex) => (
              <Cell
                key={colIndex}
                role="cell"
                style={{ width: `${col.width}%` }}
              >
                {getCellValue(row, col?.value, parentTestId)}
              </Cell>
            ))}
          </DataRowWrapper>
        ))}
        {isSilentLoading && (
          <ScrollLoaderWrapper>
            <LTLoader size="small" />
          </ScrollLoaderWrapper>
        )}
      </RowGroup>
    </div>
  );
};

export default HistoryList;
